import * as React from 'react';

import {
  createStyles,
  Grid,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  WithStyles,
  withStyles
} from '@material-ui/core';
import { ResponsiveLine, Serie } from '@nivo/line';
import numeral from 'numeral';

import { ITrend } from '@lieuu/core';

import getChartTheme from './getChartTheme';
import InfoBubble from './InfoBubble';

const styles = (theme: Theme) =>
  createStyles({
    lineChart: {
      position: 'relative',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(5),
      marginLeft: 'auto',
      marginRight: 'auto',
      height: 300,
      width: '100%'
    },
    title: {
      fontSize: '1em'
    }
  });

type ILineYearlyChartProps = {
  variant?: 'dollars' | 'percent' | 'plain';
  trendLieuucation: ITrend;
  trendCbsa?: ITrend;
};

const LineYearlyChart: React.FC<ILineYearlyChartProps> = ({
  variant,
  trendLieuucation,
  trendCbsa
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));

  const chartTheme = getChartTheme(isSmall, theme);

  const data: Serie[] = [];

  const lieuucationData = Object.keys(trendLieuucation)
    .map((key) => ({
      x: `${Number(key) + 2}`,
      y: trendLieuucation[key]
    }))
    .filter((e) => e.y !== -1);

  data.push({
    id: 'Micromarket',
    data: lieuucationData
  });

  if (trendCbsa) {
    const cbsaData = Object.keys(trendCbsa)
      .map((key) => ({
        x: `${Number(key) + 2}`,
        y: trendCbsa[key]
      }))
      .filter((e) => e.y !== -1);

    data.push({
      id: 'City',
      data: cbsaData
    });
  }

  return (
    <ResponsiveLine
      data={data}
      colors={{ scheme: 'pastel1' }}
      enablePointLabel={false}
      margin={{ top: 25, right: 20, bottom: 40, left: 60 }}
      pointSize={10}
      pointBorderWidth={2}
      useMesh={true}
      xScale={{
        type: 'time',
        format: '%Y',
        precision: 'year'
      }}
      xFormat="time:%Y"
      yScale={{
        type: 'linear'
      }}
      enableGridY={true}
      enableGridX={false}
      curve={'natural'}
      axisBottom={{
        tickValues: isSmall ? 'every 2 years' : 'every 1 year',
        format: '%Y'
      }}
      axisLeft={{
        format: (value) =>
          variant === 'dollars'
            ? `${numeral(Number(value)).format('$0')}`
            : variant === 'plain'
            ? `${numeral(Number(value)).format('0')}`
            : `${numeral(Number(value)).format('0%')}`
      }}
      tooltip={(data) => (
        <div>
          {variant === 'dollars'
            ? numeral(data.point.data.y).format('$0,0')
            : variant === 'plain'
            ? numeral(data.point.data.y).format('0')
            : numeral(data.point.data.y).format('0%')}
        </div>
      )}
      isInteractive={true}
      legends={[
        {
          anchor: 'top-right',
          itemTextColor: chartTheme.labels.text.color,
          direction: 'row',
          justify: false,
          translateX: -15,
          translateY: -25,
          itemWidth: 65,
          itemHeight: 20,
          itemDirection: 'left-to-right',
          itemOpacity: 0.85,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1
              }
            }
          ]
        }
      ]}
      theme={chartTheme}
    />
  );
};

type IProps = WithStyles<typeof styles> &
  ILineYearlyChartProps & {
    title: string;
    description: React.ReactNode;
    wide?: boolean;
  };

const LineYearlyChartWithTitle: React.FC<IProps> = ({
  classes,
  title,
  description,
  trendLieuucation,
  trendCbsa,
  variant = 'dollars',
  wide
}) => {
  return (
    <Grid item xs={12} md={wide ? 8 : 6}>
      <Paper className={classes.lineChart}>
        <Typography className={classes.title} gutterBottom variant="overline">
          {title.toUpperCase()}
        </Typography>

        <InfoBubble title={title} body={description} />

        <LineYearlyChart
          variant={variant}
          trendLieuucation={trendLieuucation}
          trendCbsa={trendCbsa}
        />
      </Paper>
    </Grid>
  );
};

export default withStyles(styles)(LineYearlyChartWithTitle);
